/**
 * React Hooks for Safe Custody
 *
 * Provides custom hooks for managing Safe Custody packet state
 * using React Query for caching and synchronization.
 */

'use client';

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseInfiniteQueryOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { directApiClient } from '@/lib/api/direct-client';
import { SMART_CACHE_CONFIG } from '../query-config';
import {
  createSafeCustodyPacketAction,
  updateSafeCustodyPacketAction,
  deleteSafeCustodyPacketAction,
  addDocumentAction,
  removeDocumentAction,
  deleteDocumentAction,
  restoreDocumentAction
} from './actions';
import type {
  SafeCustodyPacket,
  SafeCustodyPacketDetail,
  SafeCustodyFilters,
  SafeCustodyPaginatedResponse,
  SafeCustodyTimelineEvent,
  CreateSafeCustodyPacketRequest,
  UpdateSafeCustodyPacketRequest,
  SafeCustodyContactSearch
} from './types';

/**
 * Query key factory for safe custody
 */
export const safeCustodyKeys = {
  all: ['safe-custody'] as const,
  lists: () => [...safeCustodyKeys.all, 'list'] as const,
  list: (filters?: SafeCustodyFilters) =>
    [...safeCustodyKeys.lists(), filters] as const,
  details: () => [...safeCustodyKeys.all, 'detail'] as const,
  detail: (packetId: number) =>
    [...safeCustodyKeys.details(), packetId] as const
} as const;

/**
 * Hook to fetch safe custody packets with infinite loading
 * Used for the safe custody list page with load-more functionality
 */
export function useInfiniteSafeCustodyPackets(
  filters?: Omit<SafeCustodyFilters, 'page'>,
  options?: Omit<
    UseInfiniteQueryOptions<SafeCustodyPaginatedResponse, Error>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >
) {
  const limit = filters?.limit || 20;

  return useInfiniteQuery({
    queryKey: [...safeCustodyKeys.lists(), 'infinite', filters],
    queryFn: async ({ pageParam }): Promise<SafeCustodyPaginatedResponse> => {
      const currentPage = pageParam as number;

      // Build query params
      const params = new URLSearchParams();
      if (filters?.q) {
        params.append('q', filters.q);
      }
      if (filters?.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      if (filters?.sortOrder) {
        params.append('sortOrder', filters.sortOrder);
      }
      params.append('page', String(currentPage));
      params.append('limit', String(limit));

      const queryString = params.toString();
      const url = `/safe-custody/packets${queryString ? `?${queryString}` : ''}`;

      // Direct API call via directApiClient
      const response = await directApiClient.get<{
        items: SafeCustodyPacket[];
        meta: { totalItems: number };
      }>(url);

      const total = response.meta?.totalItems ?? response.items?.length ?? 0;
      const totalPages = Math.ceil(total / limit);

      return {
        items: response.items || [],
        total,
        page: currentPage,
        limit,
        totalPages
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    ...SMART_CACHE_CONFIG,
    ...options
  });
}

/**
 * Hook to fetch a single safe custody packet detail
 */
export function useSafeCustodyPacket(
  packetId: number | null,
  options?: Omit<
    UseQueryOptions<SafeCustodyPacketDetail, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: safeCustodyKeys.detail(packetId!),
    queryFn: async () => {
      // Direct API call via directApiClient
      return directApiClient.get<SafeCustodyPacketDetail>(
        `/safe-custody/packets/${packetId}`
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: packetId !== null,
    ...options
  });
}

/**
 * Hook to fetch timeline events for a packet
 */
export function useSafeCustodyTimeline(
  packetId: number | null,
  options?: Omit<
    UseQueryOptions<SafeCustodyTimelineEvent[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: [...safeCustodyKeys.detail(packetId!), 'timeline'] as const,
    queryFn: async () => {
      // Direct API call via directApiClient
      return directApiClient.get<SafeCustodyTimelineEvent[]>(
        `/safe-custody/packets/${packetId}/timeline`
      );
    },
    staleTime: 5 * 60 * 1000,
    enabled: packetId !== null,
    ...options
  });
}

/**
 * Hook to search contacts
 */
export function useSearchContacts(
  query: string,
  options?: Omit<
    UseQueryOptions<SafeCustodyContactSearch, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: ['contacts', 'search', query] as const,
    queryFn: async () => {
      // Direct API call via directApiClient
      const params = new URLSearchParams();
      if (query) {
        params.append('q', query);
        params.append('limit', '5');
      }
      return directApiClient.get<SafeCustodyContactSearch>(
        `/contacts?${params.toString()}`
      );
    },
    staleTime: 30 * 1000, // 30 seconds
    ...options
  });
}

/**
 * Hook to create a new safe custody packet
 */
export function useCreateSafeCustodyPacket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateSafeCustodyPacketRequest) => {
      const result = await createSafeCustodyPacketAction(request);
      if (!result.success) {
        throw new Error(result.error || 'Failed to create packet');
      }
      return result.data!;
    },
    onSuccess: () => {
      toast.success('Packet created successfully');
      queryClient.invalidateQueries({ queryKey: safeCustodyKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create packet');
    }
  });
}

/**
 * Hook to update a safe custody packet
 */
export function useUpdateSafeCustodyPacket(packetId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateSafeCustodyPacketRequest) => {
      const result = await updateSafeCustodyPacketAction(packetId, request);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update packet');
      }
      return result.data!;
    },
    onSuccess: () => {
      toast.success('Packet updated successfully');
      queryClient.invalidateQueries({ queryKey: safeCustodyKeys.all });
      queryClient.invalidateQueries({
        queryKey: safeCustodyKeys.detail(packetId)
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update packet');
    }
  });
}

/**
 * Hook to delete a safe custody packet
 */
export function useDeleteSafeCustodyPacket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (packetId: number) => {
      const result = await deleteSafeCustodyPacketAction(packetId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete packet');
      }
    },
    onSuccess: () => {
      toast.success('Packet deleted successfully');
      queryClient.invalidateQueries({ queryKey: safeCustodyKeys.all });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete packet');
    }
  });
}

/**
 * Hook to add a document to a packet
 */
export function useAddDocument(packetId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const result = await addDocumentAction(packetId, name);
      if (!result.success) {
        throw new Error(result.error || 'Failed to add document');
      }
      return result.data!;
    },
    onSuccess: () => {
      toast.success('Document added successfully');
      queryClient.invalidateQueries({
        queryKey: safeCustodyKeys.detail(packetId)
      });
      queryClient.invalidateQueries({ queryKey: safeCustodyKeys.lists() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add document');
    }
  });
}

/**
 * Hook to remove a document from a packet (soft delete - moves to removed documents)
 */
export function useRemoveDocument(packetId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      reason
    }: {
      documentId: number;
      reason?: string;
    }) => {
      const result = await removeDocumentAction(packetId, documentId, reason);
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove document');
      }
      return result.data!;
    },
    onSuccess: () => {
      toast.success('Document removed successfully');
      queryClient.invalidateQueries({
        queryKey: safeCustodyKeys.detail(packetId)
      });
      queryClient.invalidateQueries({ queryKey: safeCustodyKeys.lists() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove document');
    }
  });
}

/**
 * Hook to permanently delete a document from a packet
 */
export function useDeleteDocument(packetId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      reason
    }: {
      documentId: number;
      reason?: string;
    }) => {
      const result = await deleteDocumentAction(packetId, documentId, reason);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete document');
      }
      return result.data!;
    },
    onSuccess: () => {
      toast.success('Document deleted permanently');
      queryClient.invalidateQueries({
        queryKey: safeCustodyKeys.detail(packetId)
      });
      queryClient.invalidateQueries({ queryKey: safeCustodyKeys.lists() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete document');
    }
  });
}

/**
 * Hook to restore a removed document back to active
 */
export function useRestoreDocument(packetId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: number) => {
      const result = await restoreDocumentAction(packetId, documentId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to restore document');
      }
      return result.data!;
    },
    onSuccess: () => {
      toast.success('Document restored successfully');
      queryClient.invalidateQueries({
        queryKey: safeCustodyKeys.detail(packetId)
      });
      queryClient.invalidateQueries({ queryKey: safeCustodyKeys.lists() });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to restore document');
    }
  });
}
